import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse, stringify } from 'qs';
import {
  Tag,
  Table,
  Button,
  Tooltip,
  Input,
  Popconfirm,
  Spin,
  message,
} from 'antd';
import { PlusCircleOutlined, InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import { captureAnalytics } from '../../../actions/preload';
import './manage-labels.scss';

const contrastingTextColor = (backgroundColor) => {
  const hex = backgroundColor?.replace('#', '');
  if (!hex || ![3, 6].includes(hex.length) || !/^[0-9a-f]+$/i.test(hex)) return undefined;

  const normalizedHex = hex.length === 3
    ? hex.split('').map((character) => character.repeat(2)).join('')
    : hex;
  const [red, green, blue] = normalizedHex.match(/.{2}/g).map((value) => parseInt(value, 16));
  const luminance = (0.299 * red) + (0.587 * green) + (0.114 * blue);

  return luminance > 140 ? '#21152f' : '#fff';
};

const parseOptions = {
  ignoreQueryPrefix: true,
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

const PAGE_SIZE_OPTIONS = ['10', '20', '50'];
const DEFAULT_PAGE_SIZE = 10;

export class ManageLabels extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      searchValue: '',
      // The search box writes searchValue on every keystroke but only submits
      // on Enter, clear, or a department filter change, so the term the table
      // is actually showing lives here. Refetches must read this, never
      // searchValue, or they apply text the user typed but never submitted.
      submittedSearch: '',
      canManageLabel: false,
      isDepartmentLabelsEnabled: false,
      isLlmDerivedLabelsEnabled: false,
    };
  }

  async componentDidMount() {
    const {
      getLabelsByPage, getUserInfo, getDocumentCategories, getOrganizationDepartments, isAdmin,
    } = this.props;
    const [
      ,
      canManageLabel,
      isDepartmentLabelsEnabled,
      hasLlmDerivedLabels,
      hasAiExtraction,
    ] = await Promise.all([
      getUserInfo(),
      verifyFeatureAccess('manage_label'),
      verifyFeatureAccess('department_labels'),
      verifyFeatureAccess('llm_derived_labels'),
      verifyFeatureAccess('ai_extraction'),
      getDocumentCategories(),
      getLabelsByPage(1, this.state.pageSize),
    ]);
    const isLlmDerivedLabelsEnabled = hasLlmDerivedLabels && hasAiExtraction;
    // Only an admin can scope a label to a department they do not belong to, so
    // only an admin needs the organization-wide list. Fetched here rather than
    // on modal open so the picker never renders empty and then fills in.
    if (isDepartmentLabelsEnabled && isAdmin && getOrganizationDepartments) {
      await getOrganizationDepartments();
    }
    this.setState({
      canManageLabel,
      isDepartmentLabelsEnabled,
      isLlmDerivedLabelsEnabled,
    });
  }

  getQueryData = async (page = 1) => {
    const { location, headers, getLabelsByQuery } = this.props;
    const totalPages = (headers ? parseInt(headers.total_count, 10) : 1);
    let requestedPage = page;
    let params = {};
    if (location.search) {
      params = parse(location.search, parseOptions);
      requestedPage = parseInt((params.page <= totalPages ? params.page : 1), 10);
    }
    params.page = requestedPage;
    params.page_size = this.state.pageSize;
    const query = stringify(params, stringifyOptions);
    await getLabelsByQuery(query);
  };

  // Re-fetch the table exactly as the user left it (search or department
  // filter, page, and page size) after the add/edit dialog saves. The dialog otherwise
  // reloads page one while this page still reports the old page to the
  // paginator. An edit can move the only row on the last page out of the
  // search results, so step back to the last page that still has rows.
  refreshLabels = async () => {
    const { getLabelsByQuery } = this.props;
    const { currentPage, pageSize, submittedSearch } = this.state;
    const fetchPage = (page) => getLabelsByQuery(stringify(
      { page, page_size: pageSize, contains: submittedSearch.trim() ? submittedSearch : undefined },
      stringifyOptions,
    ));
    // Read the rows off the response rather than this.props: the store has been
    // updated by now but the connected re-render may not have reached us yet.
    const response = await fetchPage(currentPage);
    if (response?.error) return;
    const rows = response?.data?.labels || [];
    if (rows.length > 0 || currentPage <= 1) return;
    const lastPage = Math.max(1, parseInt(response?.data?.meta?.total_pages, 10) || 1);
    const page = Math.min(currentPage - 1, lastPage);
    this.setState({ currentPage: page });
    await fetchPage(page);
  };

  openAddLabelModal = (label, aiConversionSource = null) => {
    const {
      openModal,
      isAutoLabelingEnabled,
      isAdmin,
      departments: userDepartments,
      allOrganizationDepartments,
      allDocumentCategories,
    } = this.props;
    const { isDepartmentLabelsEnabled, isLlmDerivedLabelsEnabled } = this.state;
    const selectedLabel = label && label.id ? label : {};
    const modalType = {
      type: 'ADD_LABEL',
      data: {
        label: selectedLabel,
        isAutoLabelingEnabled,
        isDepartmentLabelsEnabled,
        isLlmDerivedLabelsEnabled,
        isAdmin,
        userDepartments,
        // Admins scope labels to any department in the organization, not only
        // the ones they belong to.
        organizationDepartments: allOrganizationDepartments || [],
        documentCategories: allDocumentCategories.categories || [],
        aiConversionSource,
        pageSize: this.state.pageSize,
        onSaved: this.refreshLabels,
      },
    };
    return openModal(modalType);
  };

  startAiConversion = (label) => {
    captureAnalytics('label_ai_conversion_started', {
      label_id: label.id,
      source: 'manage_labels',
    });
    this.openAddLabelModal(label, 'manage_labels');
  };

  userCanEditLabels = () => {
    const { canManageLabel } = this.state;
    const { isAdmin } = this.props;
    return (canManageLabel && isAdmin) || !canManageLabel;
  };

  getLabelRowProps = (label) => {
    const openLabel = (event) => {
      const interactiveElement = event.target.closest(
        'button, a, input, textarea, select, [role="button"]',
      );
      if (interactiveElement && event.currentTarget.contains(interactiveElement)) return;
      this.openAddLabelModal(label);
    };

    return {
      onClick: openLabel,
      onKeyDown: (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openLabel(event);
      },
      tabIndex: 0,
      'aria-label': `Edit ${label.name} label`,
    };
  };

  handleTableChange = (pagination, filters) => {
    const { current, pageSize } = pagination;
    const { pageSize: previousPageSize } = this.state;
    const page = pageSize !== previousPageSize ? 1 : current;
    const { department } = filters;
    let { submittedSearch } = this.state;
    if (department && department.length > 0) {
      submittedSearch = department[0] !== 'all' ? department[0] : '';
      this.setState({
        searchValue: submittedSearch,
        submittedSearch,
      });
    }
    if (pageSize !== previousPageSize) {
      captureAnalytics('labels_page_size_changed', { page_size: pageSize });
    }
    this.setState({ currentPage: page, pageSize });
    const { getLabelsByQuery: fetchLabels } = this.props;
    fetchLabels(stringify(
      { page, page_size: pageSize, contains: submittedSearch.trim() ? submittedSearch : undefined },
      stringifyOptions,
    ));
  };

  searchLabels = (value) => {
    const { paramTools, getLabelsByQuery } = this.props;
    const { params } = paramTools;
    if (params.contains === '' || value === '') {
      delete params.contains;
      params.page = 1;
    } else {
      params.contains = value;
      params.page = 1;
    }
    params.page_size = this.state.pageSize;
    const query = stringify(params, stringifyOptions);
    this.setState({ searchValue: value, submittedSearch: value, currentPage: 1 });
    const emptySearch = (value.trim() === '');
    if (emptySearch) return getLabelsByQuery(stringify({ page: 1, page_size: this.state.pageSize }, stringifyOptions));
    getLabelsByQuery(query);
  };

  deleteLabel = async (label) => {
    const { deleteLabel } = this.props;
    await deleteLabel(label.id);
    const { error, errorCode } = this.props;
    if (error && errorCode === 403) {
      message.error('Cannot delete label. You need permission to perform this action');
    }

    this.getQueryData();
  };

  renderAutomaticRule = (_, label) => {
    const canEditLabels = this.userCanEditLabels();

    if (!label.auto_label) {
      return (
        <span className="manage-labels__rule-empty">No automatic rule</span>
      );
    }

    if (label.auto_label_mode === 'ai') {
      const exceptionCount = label.auto_label_exclusions ? 1 : 0;
      const excludedCategories = label.excluded_document_categories || [];
      return (
        <div className="manage-labels__rule">
          <Tooltip title={label.auto_label_instructions} placement="bottomLeft">
            <span className="manage-labels__rule-title">
              {label.auto_label_instructions}
            </span>
          </Tooltip>
          <div className="manage-labels__rule-details">
            <span className="manage-labels__rule-type manage-labels__rule-type--ai">AI rule</span>
            {exceptionCount > 0 && (
              <span>
                <span className="manage-labels__rule-divider">·</span>
                {exceptionCount}
                {' '}
                exception
              </span>
            )}
            {excludedCategories.length > 0 && (
              <Tooltip
                title={`Excluded categories: ${excludedCategories.map((category) => category.name).join(', ')}`}
                placement="bottomLeft"
              >
                <span className="manage-labels__rule-exclusions">
                  <span className="manage-labels__rule-divider">·</span>
                  {excludedCategories.length}
                  {' '}
                  excluded
                  {' '}
                  {excludedCategories.length === 1 ? 'category' : 'categories'}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      );
    }

    const { positive_keywords: positiveKeywords = [], negative_keywords: negativeKeywords = [] } = label;
    const keywordSummary = positiveKeywords.slice(0, 3).join(', ');
    const remainingPositiveCount = Math.max(positiveKeywords.length - 3, 0);
    const exclusionCount = negativeKeywords.length;

    return (
      <div className="manage-labels__rule">
        <Tooltip title={positiveKeywords.join(', ')} placement="bottomLeft">
          <span className="manage-labels__rule-title">
            {keywordSummary ? `Matches ${keywordSummary}` : 'No match keywords'}
            {remainingPositiveCount > 0 ? ` +${remainingPositiveCount}` : ''}
          </span>
        </Tooltip>
        <div className="manage-labels__rule-details">
          <span className="manage-labels__rule-type manage-labels__rule-type--keyword">Keyword rule</span>
          {exclusionCount > 0 && (
            <Tooltip title={`Excludes: ${negativeKeywords.join(', ')}`} placement="bottomLeft">
              <span className="manage-labels__rule-exclusions">
                <span className="manage-labels__rule-divider">·</span>
                {exclusionCount}
                {' '}
                exclusion
                {exclusionCount === 1 ? '' : 's'}
              </span>
            </Tooltip>
          )}
          {canEditLabels && (
            <>
              <span className="manage-labels__rule-divider">·</span>
              <Button
                type="link"
                size="small"
                onClick={() => this.startAiConversion(label)}
                className="manage-labels__upgrade-button"
              >
                Convert to AI
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      currentPage,
      pageSize,
      searchValue,
      isDepartmentLabelsEnabled,
      isLlmDerivedLabelsEnabled,
    } = this.state;
    const {
      data: labels,
      headers,
      isLoading,
      departments: userDepartments,
      isAutoLabelingEnabled,
    } = this.props;
    const AllDepartments = [{ name: 'All departments' }, ...userDepartments];
    const totalResults = headers ? headers.total_count : 1;
    const canEditLabels = this.userCanEditLabels();
    let labelDescription = 'Create labels to organize, filter, and route incoming documents.';
    if (isAutoLabelingEnabled) {
      labelDescription = isLlmDerivedLabelsEnabled
        ? `${labelDescription} For automatic labels, describe which documents should receive them and any exceptions.`
        : `${labelDescription} Add positive and negative keywords to control automatic labeling.`;
    }
    const ruleColumns = isLlmDerivedLabelsEnabled
      ? [
        {
          title: (
            <Tooltip title="Describe which incoming documents should receive each label.">
              Automatic rule
              <InfoCircleOutlined style={{ paddingLeft: '3px' }} />
            </Tooltip>
          ),
          key: 'automatic_rule',
          render: this.renderAutomaticRule,
        },
      ]
      : [
        {
          title: (
            <Tooltip title="Associate additional words with this label so documents containing these words will also be labeled with this when autolabeled.">
              Positive Keywords
              <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
            </Tooltip>
          ),
          key: 'positive_keywords',
          dataIndex: 'positive_keywords',
          render: (keyword) => keyword.map((word) => <Tag color="blue" key={word}>{word}</Tag>),
        },
        {
          title: (
            <Tooltip title="Use negative keywords to prevent documents containing these words from being automatically labeled.">
              Negative Keywords
              <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
            </Tooltip>
          ),
          key: 'negative_keywords',
          dataIndex: 'negative_keywords',
          render: (keyword) => keyword.map((word) => <Tag color="red" key={word}>{word}</Tag>),
        },
      ];
    let columns = [
      {
        title: 'Label',
        dataIndex: 'name',
        key: 'name',
        render: (label, labelRecord) => {
          const labelColor = labelRecord.color;
          const labelTextColor = contrastingTextColor(labelColor);
          return (
            <Tag
              color={labelColor}
              style={{ color: labelTextColor }}
              className="manage-labels__label-chip"
              key={label}
            >
              {label}
            </Tag>
          );
        },
      },
      ...ruleColumns,
      {
        title: (
          <Tooltip title="These labels will be used to autolabel records when they are received">
            Status
            <InfoCircleOutlined style={{ paddingLeft: '3px', marginTop: '6px', height: '10px' }} />
          </Tooltip>),
        key: 'auto_label',
        dataIndex: 'auto_label',
        render: (autolabel) => (
          <span className={`manage-labels__status ${autolabel ? 'manage-labels__status--on' : ''}`}>
            <span className="manage-labels__status-dot" />
            {autolabel ? 'Automatic' : 'Manual'}
          </span>
        ),
      },
      {
        title: 'Department',
        key: 'department',
        dataIndex: 'department',
        filters: AllDepartments.map((department) => ({
          value: department.name === 'All departments' ? 'all' : department.name,
          text: department.name,
        })),
        filterMultiple: false,
        // A label can now be scoped to several departments. `departments` is the
        // multi-department field; `department` is the legacy single name, kept
        // for a label the backfill has not reached. An organization-wide label
        // has neither and renders as an empty cell, as it always did.
        render: (department, label) => {
          const names = (label.departments || []).map((entry) => entry.name);
          const text = names.length > 0 ? names.join(', ') : department;
          if (!text) return null;

          // Truncates in the cell; the tooltip carries the full list so a label
          // in many departments stays readable at column width.
          return (
            <Tooltip title={text}>
              <span className="manage-labels__departments">{text}</span>
            </Tooltip>
          );
        },
      },
      {
        title: '',
        key: 'edit',
        align: 'left',
        render: (_, label) => (
          <span className="manage-labels__actions">
            <Button onClick={() => this.openAddLabelModal(label)} className="manage-labels__edit-button">
              Edit
            </Button>
            {canEditLabels && (
              <Popconfirm title="Delete this Label? (This action is permanent and cannot be reversed)" onConfirm={() => this.deleteLabel(label)}>
                <Button type="link" danger size="small" className="manage-labels__delete-button">Delete</Button>
              </Popconfirm>
            )}
          </span>
        ),
      },
    ];

    if (isLoading) return (<Spin size="large" />);
    columns = columns.filter((column) => column.dataIndex !== 'department' || isDepartmentLabelsEnabled);

    return (
      <section className="manage-labels">
        <header className="manage-labels__header">
          <div className="manage-labels__heading">
            <span className="manage-labels__eyebrow">Inbox organization</span>
            <h2>Manage labels</h2>
            <p>{labelDescription}</p>
          </div>
        </header>

        <div className="manage-labels__toolbar">
          {canEditLabels ? (
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={this.openAddLabelModal}
              className="manage-labels__add-button"
            >
              Add label
            </Button>
          ) : <span />}
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search labels"
            value={searchValue}
            onChange={(event) => {
              const { value } = event.target;
              this.setState({ searchValue: value });
              if (value === '') this.searchLabels('');
            }}
            onPressEnter={(event) => this.searchLabels(event.currentTarget.value)}
            allowClear
            className="manage-labels__search"
          />
        </div>

        <div className="manage-labels__table-shell">
          <Table
            size="small"
            dataSource={labels}
            columns={columns}
            rowKey={(record) => record.id}
            onRow={this.getLabelRowProps}
            onChange={this.handleTableChange}
            className="manage-labels__table"
            pagination={{
              position: ['bottomCenter'],
              total: totalResults,
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </div>
      </section>
    );
  }
}

ManageLabels.defaultProps = {
  isLoading: false,
  data: [],
  headers: {
    page: 1,
    'per-page': '10',
    total_count: 1,
  },
  departments: [],
  getOrganizationDepartments: null,
  allOrganizationDepartments: [],
  allDocumentCategories: { categories: [] },
  paramTools: {
    push: () => {},
    params: {},
    stringify: () => {},
    stringifyOptions: {},
  },
  errorCode: '',
};

ManageLabels.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  headers: PropTypes.shape({
    page: PropTypes.number,
    'per-page': PropTypes.string,
    total_count: PropTypes.number,
  }),
  getLabelsByPage: PropTypes.func.isRequired,
  paramTools: PropTypes.shape({
    params: PropTypes.object,
    stringify: PropTypes.func,
    stringifyOptions: PropTypes.object,
    push: PropTypes.func,
  }),
  location: PropTypes.object.isRequired,
  deleteLabel: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  getLabelsByQuery: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getOrganizationDepartments: PropTypes.func,
  allOrganizationDepartments: PropTypes.array,
  error: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  departments: PropTypes.array,
  allDocumentCategories: PropTypes.shape({
    categories: PropTypes.array,
  }),
  isAutoLabelingEnabled: PropTypes.bool.isRequired,
};

export default connect((state) => ({
  data: state.labels.data,
  isLoading: state.labels.isLoading,
  isAdmin: state.userProfile.isAdmin,
  headers: state.labels.headers,
  error: state.labels.error,
  errorCode: state.labels.errorCode,
  allDocumentCategories: state.userProfile.allDocumentCategories,
  allOrganizationDepartments: state.userProfile.allOrganizationDepartments,
}))(ManageLabels);
